import React, { useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { messages } from '../messages';

const styles = cssMap({
	borderedBox: {
		backgroundColor: token('color.background.neutral.subtle'),
		borderRadius: token('radius.small'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		overflow: 'hidden',
	},
	header: {
		paddingTop: token('space.100'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.150'),
		cursor: 'pointer',
		width: '100%',
	},
	content: {
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
	},
});

type ExpandableSectionProps = {
	title: string;
	children: React.ReactNode;
	defaultExpanded?: boolean;
	testId?: string;
};

/**
 * A collapsible section with a chevron header, matching the debugger's existing
 * expand/collapse pattern. Content is only mounted when expanded.
 */
export const ExpandableSection = ({
	title,
	children,
	defaultExpanded = false,
	testId,
}: ExpandableSectionProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [isExpanded, setIsExpanded] = useState(defaultExpanded);

	return (
		<Box xcss={styles.borderedBox} testId={testId}>
			<Pressable
				onClick={() => setIsExpanded(!isExpanded)}
				xcss={styles.header}
				testId={testId ? `${testId}-toggle` : undefined}
			>
				<Inline space="space.100" alignBlock="center">
					{isExpanded ? (
						<ChevronDownIcon
							label={formatMessage(messages.ai_debugger_modal_collapse_label)}
							color="currentColor"
						/>
					) : (
						<ChevronRightIcon
							label={formatMessage(messages.ai_debugger_modal_expand_label)}
							color="currentColor"
						/>
					)}
					<Text size="small" weight="bold">
						{title}
					</Text>
				</Inline>
			</Pressable>
			{isExpanded && (
				<Box xcss={styles.content} testId={testId ? `${testId}-content` : undefined}>
					{children}
				</Box>
			)}
		</Box>
	);
};
